import type { GpsDetectionResult } from '@capi/shared';

/**
 * Cliente del API.
 *
 * Responsabilidades: llevar el token, renovarlo cuando expira y distinguir con
 * claridad un fallo de red de un rechazo del servidor. Esa distinción es la
 * pieza central de la cola de reintento: un fallo de red se reintenta, un
 * rechazo del servidor no, porque reintentarlo daría el mismo resultado y
 * dejaría al encuestador atascado.
 */

const TOKEN_KEY = 'capi.accessToken';
const REFRESH_KEY = 'capi.refreshToken';
const DEVICE_KEY = 'capi.deviceId';

export class NetworkError extends Error {
  constructor(message = 'Sin conexión con el servidor.') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Reintentar no cambiaría el resultado. */
  get isPermanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 408 && this.status !== 429;
  }
}

/* -------------------------------------------------------------------------- */
/*                                 Sesión                                     */
/* -------------------------------------------------------------------------- */

export const session = {
  get accessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  /**
   * Identificador estable del dispositivo. Permite al supervisor ver desde qué
   * aparato captura cada encuestador, que es un indicio útil cuando una
   * entrevista aparece con un recorrido imposible.
   */
  get deviceId(): string {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  },
};

const APP_VERSION = '1.0.0';

/* -------------------------------------------------------------------------- */
/*                               Peticiones                                   */
/* -------------------------------------------------------------------------- */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** No adjunta el token (acceso y renovación). */
  anonymous?: boolean;
  /** Evita el reintento con token renovado, para no caer en un bucle. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  // Varias peticiones pueden recibir 401 a la vez; una sola renovación sirve
  // para todas y evita que la rotación de refresh tokens se pise a sí misma y
  // revoque la sesión por sospecha de reuso.
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = session.refreshToken;
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken, deviceId: session.deviceId }),
      });
      if (!response.ok) {
        session.clear();
        return false;
      }
      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      session.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      // Fallo de red: la sesión puede seguir siendo válida, así que NO se
      // limpia. Limpiarla expulsaría al encuestador por estar sin señal.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, skipRefresh = false, signal } = options;

  const headers: Record<string, string> = {
    'X-Device-Id': session.deviceId,
    'X-App-Version': APP_VERSION,
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!anonymous) {
    const token = session.accessToken;
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError();
  }

  if (response.status === 401 && !anonymous && !skipRefresh) {
    if (await refreshSession()) {
      return request<T>(path, { ...options, skipRefresh: true });
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const detail = payload as {
      message?: string | string[];
      fieldErrors?: Record<string, string[]>;
    } | null;
    const message = Array.isArray(detail?.message)
      ? detail.message.join(' ')
      : (detail?.message ?? `Error ${response.status}`);
    throw new ApiError(response.status, message, detail?.fieldErrors);
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/* -------------------------------------------------------------------------- */
/*                            Endpoints tipados                               */
/* -------------------------------------------------------------------------- */

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    code: string;
    name: string;
    role: string;
    mustChangePassword: boolean;
    permissions: string[];
    birthdayRule: 'proximo' | 'ultimo' | null;
  };
}

export const api = {
  login(identifier: string, password: string) {
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      anonymous: true,
      body: { identifier, password, app: 'field', deviceId: session.deviceId },
    });
  },

  logout() {
    return request<void>('/auth/logout', { method: 'POST' });
  },

  me() {
    return request<{
      id: string;
      code: string;
      name: string;
      role: string;
      mustChangePassword: boolean;
      birthdayRule: 'proximo' | 'ultimo' | null;
      supervisor: { id: string; name: string; code: string } | null;
    }>('/auth/me');
  },

  changePassword(currentPassword: string, newPassword: string) {
    return request<void>('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword, confirmPassword: newPassword },
    });
  },

  /* ------------------------- Consumo de datos --------------------------- */

  /**
   * Por qué red está entrando la petición.
   *
   * El navegador no puede decirlo —`navigator.connection.type` no existe— así
   * que el servidor resuelve el proveedor desde la dirección de origen.
   */
  connectionContext() {
    return request<{
      connection: 'movil' | 'wifi' | 'desconocida';
      asn: number | null;
      ispName: string | null;
      known: boolean;
      privateNetwork: boolean;
    }>('/telemetry/connection');
  },

  reportDataUsage(body: {
    deviceId: string | null;
    samples: {
      bytes: number;
      connection: 'movil' | 'wifi' | 'desconocida';
      source: 'asn' | 'manual' | 'desconocido';
      asn: number | null;
      startedAt: string;
      endedAt: string;
    }[];
  }) {
    return request<{ accepted: number }>('/telemetry/data-usage', { method: 'POST', body });
  },

  myDataUsage() {
    return request<{ movil: number; wifi: number; desconocida: number; movilHoy: number }>(
      '/telemetry/data-usage/mine',
    );
  },

  fieldPackage(wave = 'W1') {
    return request<FieldPackage>(`/instrument/field-package?wave=${wave}`);
  },

  myAssignments() {
    return request<Assignment[]>('/assignments/mine');
  },

  dispositions() {
    return request<{
      dispositions: DispositionDef[];
      timeSlots: { code: string; label: string }[];
      maxRevisitAttempts: number;
      revisitNote: string;
    }>('/interviews/dispositions');
  },

  outOfPolygonReasons() {
    return request<{ code: string; label: string; description: string }[]>(
      '/geo/out-of-polygon-reasons',
    );
  },

  sectionGeoJson(seccion: number) {
    return request<GeoJsonFeatureCollection>(`/geo/seccion/${seccion}/geojson`);
  },

  clusterGeoJson(clusterId: string) {
    return request<GeoJsonFeatureCollection>(`/geo/cluster/${clusterId}/geojson`);
  },

  detectGps(input: {
    latitude: number;
    longitude: number;
    accuracyMeters?: number | null;
    expectedFrameSeccion?: number | null;
    expectedClusterId?: string | null;
  }) {
    return request<GpsDetection>('/geo/detect', { method: 'POST', body: input });
  },

  openInterview(input: {
    clientUuid: string;
    assignmentId: string;
    isPilot: boolean;
    startedAt: string;
  }) {
    return request<OpenInterviewResponse>('/interviews/open?wave=W1', {
      method: 'POST',
      body: { ...input, deviceId: session.deviceId, appVersion: APP_VERSION },
    });
  },

  assignI6(
    clientUuid: string,
    knownPersonajeIds: string[],
    /** Qué contestó I1 por personaje, para preferir a los que conoce bien. */
    awareness: Record<string, string>,
  ) {
    return request<{ i6PersonajeIds: string[]; alreadyAssigned: boolean }>(
      '/interviews/assign-i6?wave=W1',
      { method: 'POST', body: { clientUuid, knownPersonajeIds, awareness } },
    );
  },

  sync(batch: { interviews: unknown[]; households: unknown[] }) {
    return request<SyncResponse>('/interviews/sync?wave=W1', { method: 'POST', body: batch });
  },

  myInterviews() {
    return request<
      {
        id: string;
        clientUuid: string;
        status: string;
        startedAt: string;
        durationMinutes: number | null;
        gpsStatus: string | null;
        cluster: { id: string; seccion: number; reference: string };
      }[]
    >('/interviews/mine');
  },
};

/* -------------------------------------------------------------------------- */
/*                         Tipos de las respuestas                            */
/* -------------------------------------------------------------------------- */

export interface FieldPackage {
  wave: { id: string; code: string; label: string; status: string };
  instrumentId: string;
  version: string;
  definition: InstrumentDefinition;
  activeTrims: string[];
  trims: { key: string; order: number; description: string; mode: string; enabled: boolean; rowsKept?: number; respondentShare?: number; assignmentSize?: number }[];
  experiments: {
    key: string;
    questionCode: string;
    cells: { id: string; personajeId: string; labelText: string }[];
  }[];
  personajes: { id: string; cardName: string }[];
  parties: { id: string; label: string }[];
  coalitions: { id: string; label: string; spokenLabel: string }[];
  block4Officials: { id: string; questionCode: string; name: string; office: string }[];
  codebooks: {
    id: string;
    label: string;
    questions: string[];
    allowsOther: boolean;
    entries: { code: string; label: string; synonyms: string[] }[];
  }[];
}

/** Espejo de la definición de @capi/shared, serializada. */
export interface InstrumentDefinition {
  version: string;
  wave: string;
  methodologyVersion: string;
  estimatedMinutes: { min: number; max: number };
  blocks: InstrumentBlock[];
  frozenForPanelComparability: string[];
}

export interface InstrumentBlock {
  id: number;
  title: string;
  estimatedMinutes: number;
  estimatedMinutesMax?: number;
  readAloudIntro?: string;
  orderingRationale?: string;
  isCore?: boolean;
  questions: InstrumentQuestion[];
}

export interface InstrumentQuestion {
  /** Texto con el que se rellena la respuesta al llegar a la pregunta. */
  defaultText?: string;
  /** Rellena la respuesta con el contexto de la entrevista (sección/colonia). */
  prefillFrom?: 'gps-location';
  code: string;
  kind: string;
  text: string;
  interviewerNote?: string;
  methodologyNote?: string;
  options?: InstrumentOption[];
  rowSource?: { kind: string; rows?: { code: string; label: string; rotates?: boolean; rotationGroup?: string }[]; assignmentKey?: string };
  rotateRows?: boolean;
  rotateOptions?: boolean;
  showIf?: unknown;
  showRowIf?: unknown;
  terminateIf?: { condition: unknown; disposition: string; message: string };
  min?: number;
  max?: number;
  maxSelections?: number;
  required?: boolean;
  allowRefusal?: boolean;
  specifyLabel?: string;
  codebookId?: string;
  codeInOffice?: boolean;
  isPii?: boolean;
  trimKey?: string;
}

export interface InstrumentOption {
  code: string;
  label: string;
  value?: number;
  nonSubstantive?: boolean;
  exclusive?: boolean;
  requiresSpecify?: boolean;
  spontaneousOnly?: boolean;
  /** La escribe el servidor (cierre automático de I2–I5). No se dibuja. */
  systemAssigned?: boolean;
  interviewerHint?: string;
}

export interface Assignment {
  id: string;
  status: string;
  wave: { id: string; code: string; label: string; status: string };
  targetInterviews: number;
  capturedInterviews: number;
  householdsContacted: number;
  systematicSkipK: number | null;
  birthdayRule: 'proximo' | 'ultimo' | null;
  cluster: {
    id: string;
    seccion: number;
    colonia: string;
    coloniaConfirmed: boolean;
    areaCode: string;
    areaLabel: string;
    reference: string;
    stratum: { id: string; name: string };
    estimatedDwellings: number | null;
    manzanas: {
      id: string;
      cvegeo: string | null;
      orderIndex: number;
      estimatedDwellings: number | null;
      targetInterviews: number | null;
    }[];
  };
  note: string | null;
}

export interface DispositionDef {
  code: string;
  label: string;
  aapor: string;
  final: boolean;
  allowsRevisit: boolean;
  description: string;
}

/**
 * El resultado de la autodetección es EXACTAMENTE el tipo de @capi/shared, no
 * una copia con `status: string`. Si fueran dos tipos distintos, un estado
 * nuevo en el servidor compilaría sin que la PWA lo manejara, y el encuestador
 * vería una pantalla sin veredicto.
 */
export type GpsDetection = GpsDetectionResult;

export interface OpenInterviewResponse {
  interviewId: string;
  instrumentId: string;
  instrumentVersion: string;
  alreadyOpen: boolean;
  status: string;
  personajeOrder: string[];
  partyOrder: string[];
  experimentCellId: string | null;
  experimentCell: { id: string; personajeId: string; labelText: string } | null;
  i6PersonajeIds: string[];
  trimState: Record<string, boolean>;
  expectedFrameSeccion?: number;
}

export interface SyncResponse {
  interviews: {
    clientUuid: string;
    outcome: 'accepted' | 'duplicate' | 'rejected';
    interviewId?: string;
    qualityFlags?: string[];
    gpsStatus?: string | null;
    distanceToExpectedMeters?: number | null;
    message?: string;
  }[];
  households: { clientUuid: string; outcome: string; message?: string }[];
  accepted: number;
  duplicates: number;
  rejected: number;
}

export interface GeoJsonFeatureCollection {
  type: 'FeatureCollection';
  features: {
    type: 'Feature';
    properties: Record<string, unknown>;
    geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown };
  }[];
}
